import { useState, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import toast from "components/partials/toast/toast";
import { ConfirmationDialog } from "ui/confirmation-dialog";
import SelectedTouchpointsConfirmationModal from "containers/admin/clients/campaign/campaign-creative-page/components/selected-touchpoints-confirmation-modal/selected-touchpoints-confirmation-modal";
import { useDeleteTouchpoint } from "hooks/api/use-touchpoints";
import { folderKeys, touchpointKeys } from "hooks/api/query-keys";
import { getErrorMessage, getActionTouchpointTypeLabel } from "utilities";
import { TouchpointType } from "types/touchpoint";
import type { TouchpointAttributes } from "hooks/api/use-touchpoints";

const WEBPAGE_ARCHIVE_DESCRIPTION =
  "Any links to this page in other creatives will need to be updated.";

interface UseArchiveTouchpointModalProps {
  onSuccess?: () => void;
  onRemoveSelectedItem?: (id: string) => void;
  /** Optional context override for pages without a CampaignProvider (e.g. program-templates). */
  apiContext?: { clientId?: string; campaignId?: string };
  /** When true, labels in the modal use "template" instead of "email"/"creative". */
  isTemplate?: boolean;
}

export const useArchiveTouchpointModal = ({
  onSuccess,
  onRemoveSelectedItem,
  apiContext,
  isTemplate = false,
}: UseArchiveTouchpointModalProps = {}) => {
  const [itemsToArchive, setItemsToArchive] = useState<TouchpointAttributes[]>([]);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const queryClient = useQueryClient();
  const deleteMutation = useDeleteTouchpoint(apiContext);

  const openArchiveModal = useCallback((items: TouchpointAttributes | TouchpointAttributes[]) => {
    const isBulk = Array.isArray(items);
    const itemsArray = Array.isArray(items) ? items : [items];
    setItemsToArchive(itemsArray);
    setIsBulkMode(isBulk);
  }, []);

  const closeArchiveModal = useCallback(() => {
    if (deleteMutation.isPending) return;
    setItemsToArchive([]);
    setIsBulkMode(false);
  }, [deleteMutation.isPending]);

  const handleArchive = useCallback(async () => {
    if (itemsToArchive.length === 0) return;

    if (!isBulkMode) {
      const item = itemsToArchive[0];
      deleteMutation.mutate(
        { id: item.id },
        {
          onSuccess: () => {
            const typeLabel = isTemplate
              ? "Template"
              : getActionTouchpointTypeLabel(item.type);
            toast.success({ data: { title: `Archived ${typeLabel}` } });
            onSuccess?.();
            closeArchiveModal();
          },
          onError: (err) => {
            toast.error({
              data: {
                title: `Failed to archive ${isTemplate ? "template" : getActionTouchpointTypeLabel(item.type).toLowerCase()}`,
                message: getErrorMessage(err),
              },
            });
          },
        },
      );
    } else {
      const results = await Promise.allSettled(
        itemsToArchive.map((item) =>
          deleteMutation.mutateAsync({ id: item.id, meta: { skipInvalidation: true } }),
        ),
      );

      const successIds: string[] = [];
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          successIds.push(itemsToArchive[index].id);
        }
      });

      const successCount = successIds.length;
      const failCount = results.length - successCount;

      if (successCount) {
        queryClient.invalidateQueries({ queryKey: touchpointKeys.all });
        queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
        toast.success({
          data: { title: `Archived ${successCount} creative${successCount > 1 ? "s" : ""}` },
        });
        onSuccess?.();

        // Remove successful items from the list
        setItemsToArchive((prev) => prev.filter((item) => !successIds.includes(item.id)));
        successIds.forEach((id) => onRemoveSelectedItem?.(id));

        if (!failCount) {
          closeArchiveModal();
        }
      }

      if (failCount) {
        toast.error({
          data: {
            title: "Failed to archive some creatives",
            message: getErrorMessage(results.find((r) => r.status === "rejected")?.reason),
          },
        });
      }
    }
  }, [
    itemsToArchive,
    isBulkMode,
    deleteMutation,
    queryClient,
    onSuccess,
    onRemoveSelectedItem,
    closeArchiveModal,
    isTemplate,
  ]);

  const archiveModalMarkup = useMemo(() => {
    const isOpen = itemsToArchive.length > 0;
    if (!isOpen) return null;

    if (!isBulkMode) {
      const item = itemsToArchive[0];
      if (!item) return null;
      const itemLabel = isTemplate ? "template" : getActionTouchpointTypeLabel(item.type).toLowerCase();
      const headline = `Are you sure you want to archive this ${itemLabel}?`;
      const buttonText = deleteMutation.isPending ? "Archiving..." : `Archive ${itemLabel}`;
      const isWebpage = item.type === TouchpointType.LANDING_PAGE;

      return (
        <ConfirmationDialog
          open
          onClose={closeArchiveModal}
          onConfirm={handleArchive}
          intent="error"
          headline={headline}
          message={isWebpage ? WEBPAGE_ARCHIVE_DESCRIPTION : ""}
          confirmButtonText={buttonText}
          disabled={deleteMutation.isPending}
          closable={!deleteMutation.isPending}
        />
      );
    }

    const hasWebpage = itemsToArchive.some((item) => item.type === TouchpointType.LANDING_PAGE);

    return (
      <SelectedTouchpointsConfirmationModal
        title="Are you sure you want to archive these creatives?"
        description={hasWebpage ? WEBPAGE_ARCHIVE_DESCRIPTION : undefined}
        submitButtonLabel={deleteMutation.isPending ? "Archiving..." : "Archive creatives"}
        variant="error"
        onCloseCallback={closeArchiveModal}
        onConfirmCallback={handleArchive}
        selectedList={itemsToArchive}
        isSubmitDisabled={itemsToArchive.length === 0}
        isSubmitting={deleteMutation.isPending}
      />
    );
  }, [
    itemsToArchive,
    isBulkMode,
    deleteMutation.isPending,
    closeArchiveModal,
    handleArchive,
    isTemplate,
  ]);
  return {
    openArchiveModal,
    archiveModalMarkup,
  };
};
